import { useState } from "react";
import s from "./Drift.module.css";
import { Page } from "../../layout/AppShell";
import { useDriftChecks, useDriftSchedule } from "../../api/queries";
import { byUrgency, judgeablePct, state, STATE_LABEL, type DriftCheck } from "../../domain/drift";
import { Card, EmptyState, ErrorState, Segmented, Skeleton, Table } from "../../design/components";
import { formatDate, formatDateTime } from "../../lib/format";
import { t } from "../../i18n";

type View = "checks" | "schedule";

/**
 * Whether the predictions are still worth acting on.
 *
 * The screen deliberately does not have a traffic light. Three questions are
 * kept apart — have the inputs moved, is it still calibrated, has measured
 * performance fallen — and only the last says the model is wrong. Collapsing
 * them into one indicator is the thing every other drift dashboard does, and
 * the indicator is usually the wrong colour.
 *
 * The state that matters most is "cannot say yet". A prediction with a
 * ninety-day horizon made last week has not been scored; it is not right and
 * not wrong. So the most recent window — the one everybody wants covered — is
 * exactly the one that can say nothing, and an empty window drawn green is a
 * clean bill of health nobody earned.
 */
export function DriftPage() {
  const [view, setView] = useState<View>("checks");
  const checks = useDriftChecks();
  const schedule = useDriftSchedule();

  return (
    <Page
      eyebrow="Deep thinking"
      title="Do the models still describe the estate"
      actions={
        <Segmented
          value={view}
          onChange={setView}
          ariaLabel="View"
          options={[
            { value: "checks", label: "Checks" },
            { value: "schedule", label: "What is due" },
          ]}
        />
      }
    >
      {view === "checks" && (
        <>
          {checks.isPending && <Skeleton rows={6} />}
          {checks.isError && <ErrorState error={checks.error} retry={() => checks.refetch()} />}
          {checks.data && checks.data.length === 0 && (
            <EmptyState>No drift check has been run against this site yet.</EmptyState>
          )}
          {checks.data && byUrgency(checks.data).map((c) => <Check key={c.id} check={c} />)}
        </>
      )}

      {view === "schedule" && (
        <>
          {schedule.isPending && <Skeleton rows={5} />}
          {schedule.isError && <ErrorState error={schedule.error} retry={() => schedule.refetch()} />}
          {schedule.data && (
            <>
              <Card
                title={t("Due now — {n}", { n: schedule.data.due.length })}
                sub="A model whose check is overdue is a model nobody is currently watching."
              >
                {schedule.data.due.length === 0 ? (
                  <EmptyState>Every model has been checked within its cycle.</EmptyState>
                ) : (
                  <ScheduleTable rows={schedule.data.due} />
                )}
              </Card>
              <Card
                title={t("Within cycle — {n}", { n: schedule.data.waiting.length })}
                sub="Checked recently enough that another check would measure the same outcomes twice."
              >
                {schedule.data.waiting.length === 0 ? (
                  <EmptyState>Nothing is inside its cycle.</EmptyState>
                ) : (
                  <ScheduleTable rows={schedule.data.waiting} />
                )}
              </Card>
            </>
          )}
        </>
      )}
    </Page>
  );
}

function Check({ check }: { check: DriftCheck }) {
  const st = state(check);
  const pct = judgeablePct(check);
  return (
    <div
      data-testid={`drift-${check.modelKey}`}
      className={`${s.check} ${st === "drifted" ? s.drifted : st === "holding" ? s.holding : s.inconclusive}`}
    >
      <div className={s.head}>
        <span className={s.model}>{check.modelKey}</span>
        <span className={s.state}>{t(STATE_LABEL[st])}</span>
      </div>

      <div className={s.verdict}>{check.verdict}</div>

      <div className={s.counts}>
        <div className={s.count}>
          <span>{t("Predictions")}</span>
          <b>{check.predictions}</b>
        </div>
        <div className={s.count}>
          <span>{t("Decided")}</span>
          <b>{check.decided}</b>
        </div>
        {/* The figure that decides whether the rest means anything. */}
        <div className={`${s.count} ${s.pending}`}>
          <span>{t("Horizon not elapsed")}</span>
          <b>{check.pending}</b>
        </div>
        {pct != null && (
          <div className={s.count}>
            <span>{t("Judgeable")}</span>
            <b>{pct}%</b>
          </div>
        )}
        {check.changePoint && (
          <div className={s.count}>
            <span>{t("Change point")}</span>
            <b>{formatDate(check.changePoint)}</b>
          </div>
        )}
      </div>

      <div className={s.meta}>
        {t("Window {from} to {to}", { from: formatDate(check.windowFrom), to: formatDate(check.windowTo) })}
        {check.checkedAt ? t(" · checked {when}", { when: formatDateTime(check.checkedAt) }) : ""}
      </div>
    </div>
  );
}

function ScheduleTable({
  rows,
}: {
  rows: { modelKey: string; horizonDays?: number; everyDays?: number; lastChecked?: string }[];
}) {
  return (
    <Table>
      <thead>
        <tr>
          <th>{t("Model")}</th>
          <th className={s.num}>{t("Horizon")}</th>
          <th className={s.num}>{t("Every")}</th>
          <th>{t("Last checked")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.modelKey}>
            <td className={s.model}>{r.modelKey}</td>
            <td className={s.num}>{r.horizonDays != null ? t("{n} d", { n: r.horizonDays }) : "—"}</td>
            <td className={s.num}>{r.everyDays != null ? t("{n} d", { n: r.everyDays }) : "—"}</td>
            {/* "never" rather than a dash: a model never checked is the finding */}
            <td>{r.lastChecked ? formatDateTime(r.lastChecked) : <b>{t("never")}</b>}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
